import {
  CreateToastFnReturn,
  ToastId,
  useToast,
  UseToastOptions,
} from "@chakra-ui/react";
import React, { useCallback } from "react";
import { GENERIC_ERROR } from "src/constants";
import { WEGLOT_APPLY_CLASS } from "../plugins/weglot/constants";

type ErrorToastProps = {
  title: string;
  toastId?: string;
  description?: React.ReactNode;
} & UseToastOptions;

type AvelaToastFn = (options?: UseToastOptions) => ToastId;

export interface AvelaToast
  extends AvelaToastFn,
    Pick<
      CreateToastFnReturn,
      "close" | "closeAll" | "isActive" | "promise" | "update"
    > {
  error(props: ErrorToastProps): ToastId;
  genericError(): ToastId;
}

export type UseAvelaToastOptions = {
  disableStacking?: boolean;
} & UseToastOptions;

export const useAvelaToast = (
  defaultOptions?: UseAvelaToastOptions
): AvelaToast => {
  const toast = useToast(defaultOptions);
  const disableStacking = defaultOptions?.disableStacking ?? false;

  const avelaToast: AvelaToast = useCallback<AvelaToastFn>(
    (options?: UseToastOptions): ToastId => {
      if (disableStacking) {
        toast.closeAll();
      }
      if (options?.render) {
        return toast({
          position:
            options.position ?? defaultOptions?.position ?? "bottom-right",
          isClosable: true,
          ...options,
        });
      }
      return toast({
        position:
          options?.position ?? defaultOptions?.position ?? "bottom-right",
        isClosable: true,
        ...options,
        title: options?.title ? (
          <span className={WEGLOT_APPLY_CLASS}>{options.title}</span>
        ) : undefined,
        description: options?.description ? (
          <span className={WEGLOT_APPLY_CLASS}>{options.description}</span>
        ) : undefined,
      });
    },
    [defaultOptions?.position, disableStacking, toast]
  ) as AvelaToast;

  avelaToast.update = toast.update;
  avelaToast.promise = toast.promise;
  avelaToast.closeAll = toast.closeAll;
  avelaToast.close = toast.close;
  avelaToast.isActive = toast.isActive;

  avelaToast.error = ({
    title,
    toastId,
    description,
    ...toastProps
  }: ErrorToastProps): ToastId => {
    if (disableStacking) {
      toast.closeAll();
    }
    return toast({
      position: defaultOptions?.position ?? "bottom-right",
      id: toastId,
      title: <span className={WEGLOT_APPLY_CLASS}>{title}</span>,
      description:
        description !== undefined ? (
          description
        ) : (
          <span className={WEGLOT_APPLY_CLASS}>
            Please try again later or report the problem to our support team.
          </span>
        ),
      isClosable: true,
      status: "error",
      ...toastProps,
    });
  };

  avelaToast.genericError = (): ToastId => {
    return avelaToast.error(GENERIC_ERROR);
  };

  return avelaToast;
};

type Props = {
  elementRef: React.RefObject<HTMLElement | undefined>;
  children: React.ReactNode;
};
/**
 * A component that automatically adjusts the bottom margin of Chakra UI toast containers
 * based on the height of a referenced HTML element.
 *
 * This is useful when you have a fixed bottom element (like a navigation bar or footer)
 * and want toasts to appear above it without overlapping.
 *
 * @param elementRef - A React ref pointing to the HTML element whose height should be used
 *                     to set the toast container's bottom margin
 * @param children - The child components to render
 *
 * @example
 * ```tsx
 * const bottomBarRef = useRef<HTMLDivElement>(null);
 *
 * return (
 *   <AvelaToastMarginBottomDetector elementRef={bottomBarRef}>
 *     <div>
 *       <div ref={bottomBarRef} style={{ position: 'fixed', bottom: 0, height: '60px' }}>
 *         Bottom Bar
 *       </div>
 *     </div>
 *   </AvelaToastMarginBottomDetector>
 * );
 * ```
 */
export const AvelaToastMarginBottomDetector: React.FC<Props> = ({
  elementRef,
  children,
}) => {
  React.useEffect(() => {
    let marginBottom;
    if (elementRef.current) {
      marginBottom = elementRef.current.offsetHeight + "px";
    }
    if (marginBottom) {
      appendMarginBottonStyle(marginBottom);
    }

    return () => removeMarginBottonStyle();
  }, [children, elementRef]);
  return children;
};

const appendMarginBottonStyle = (marginBottom: string) => {
  let style = document.getElementById("avela-toast-margin-bottom");
  if (!style) {
    style = document.createElement("style");
    style.id = "avela-toast-margin-bottom";
    document.head.appendChild(style);
  }
  style.innerHTML = `
        #chakra-toast-manager-bottom,
        #chakra-toast-manager-bottom-right,
        #chakra-toast-manager-bottom-left {
          margin-bottom: ${marginBottom} !important;
        }
      `;
  document.head.appendChild(style);
};

const removeMarginBottonStyle = () => {
  const existing = document.getElementById("avela-toast-margin-bottom");
  if (existing) existing.remove();
};
